/**
 * Payroll workbook — generate / review / adjust / approve / export.
 *
 * Wraps the POST /api/finance/payroll/generate + /run/:id + /adjustment
 * lifecycle. Each row is renderable even when inputs are missing: blocked
 * rows show null numbers + a red "missing fields" pill so the operator can
 * open the adjustment sheet and resolve the gap with a classified correction.
 *
 * Adjustments recorded against the run are listed below the workbook so
 * there's a visible audit trail in-context.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import {
  usePayrollWorkbook,
  fetchPayrollRuns,
  type PayrollWorkbookRow,
  type PayrollAdjustment,
  type PayrollRunStatus,
  type PayrollRun,
} from './use-payroll-workbook.js';
import PayrollAdjustmentSheet from './PayrollAdjustmentSheet.js';

function fmtMoney(n: number | null): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}

function classificationColor(c: string): string {
  if (c === 'run_only') return C.textMuted;
  if (c === 'forward_going') return C.gold;
  if (c === 'effective_dated') return C.green;
  return C.textMuted;
}

function mostRecentFriday(): string {
  const d = new Date();
  // getUTCDay: Sun=0, Fri=5. Walk back to Friday.
  while (d.getUTCDay() !== 5) d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export default function PayrollWorkbook() {
  const {
    run, rows, adjustments, loading, error,
    generate, loadRun, postAdjustment, transitionStatus,
  } = usePayrollWorkbook();

  const [weekEnding, setWeekEnding] = useState(mostRecentFriday());
  const [adjustingRow, setAdjustingRow] = useState<PayrollWorkbookRow | null>(null);
  const [unlockCode, setUnlockCode] = useState('');
  const [genError, setGenError] = useState<string | null>(null);
  const [transitionError, setTransitionError] = useState<string | null>(null);

  // Existing-run awareness: avoid the "draft already exists" dead-end by
  // showing the operator Resume vs Regenerate before they click Generate.
  const [allRuns, setAllRuns] = useState<PayrollRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const existingRunForWeek = allRuns.find((r) => r.week_ending === weekEnding) || null;

  const reloadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const resp = await fetchPayrollRuns();
      setAllRuns(resp?.runs || []);
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => { reloadRuns(); }, [reloadRuns]);

  const blockedCount = rows.filter((r) => r.blocking_reason).length;
  const readyCount = rows.length - blockedCount;

  async function handleGenerate(opts: { force?: boolean } = {}) {
    setGenError(null);
    const result = await generate(weekEnding, {
      force: opts.force,
      unlockCode: unlockCode || undefined,
    });
    if (!result) setGenError(error || 'Generate failed');
    setUnlockCode('');
    await reloadRuns();
  }

  async function handleResumeExisting() {
    if (!existingRunForWeek) return;
    setGenError(null);
    await loadRun(existingRunForWeek.id);
  }

  async function handleTransition(next: PayrollRunStatus) {
    if (!run) return;
    setTransitionError(null);
    if (next === 'approved' && blockedCount > 0) {
      setTransitionError(`Cannot approve — ${blockedCount} row(s) still blocked on missing comp fields. Resolve via adjustments first.`);
      return;
    }
    const r = await transitionStatus(run.id, next);
    if (!r.ok) setTransitionError(r.error || 'Status change failed');
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* Generate bar */}
      <div style={LAYOUT.subPanel}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
          Generate payroll workbook
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>Week ending (Friday)</label>
            <input type="date" value={weekEnding} onChange={(e) => setWeekEnding(e.target.value)} style={inputStyle} />
          </div>
          {existingRunForWeek && ['reviewed', 'approved', 'exported'].includes(existingRunForWeek.status) && (
            <div>
              <label style={labelStyle}>Unlock code (required to regenerate {existingRunForWeek.status})</label>
              <input value={unlockCode} onChange={(e) => setUnlockCode(e.target.value)} placeholder="GG42" style={inputStyle} />
            </div>
          )}

          {/* Primary action adapts to existing-run state */}
          {!existingRunForWeek && (
            <button onClick={() => handleGenerate()} className="btn btn-primary" disabled={loading || runsLoading}>
              {loading ? 'Generating…' : 'Generate'}
            </button>
          )}
          {existingRunForWeek && (
            <>
              <button onClick={handleResumeExisting} className="btn btn-primary" disabled={loading}>
                Resume existing {existingRunForWeek.status}
              </button>
              <button
                onClick={() => handleGenerate({ force: true })}
                className="btn btn-ghost"
                disabled={loading}
                title="Discards the existing run and generates fresh"
              >
                Regenerate (force)
              </button>
            </>
          )}
        </div>

        {existingRunForWeek && (
          <div style={{ marginTop: 8, fontSize: 12, color: C.textMuted }}>
            Existing run for {weekEnding}: status <span style={{ color: C.gold }}>{existingRunForWeek.status}</span>
            {existingRunForWeek.driver_count != null && ` · ${existingRunForWeek.driver_count} drivers`}
            {existingRunForWeek.total_payroll != null && ` · ${fmtMoney(existingRunForWeek.total_payroll)}`}
          </div>
        )}

        {genError && (
          <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
            <div style={{ fontSize: 12, color: C.red }}>{genError}</div>
            {/draft payroll already exists/i.test(genError) && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={handleResumeExisting} className="btn btn-primary" style={{ fontSize: 12, padding: '4px 10px' }} disabled={!existingRunForWeek}>
                  Resume existing draft
                </button>
                <button onClick={() => handleGenerate({ force: true })} className="btn btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }}>
                  Regenerate (force)
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Run header */}
      {run && (
        <div style={LAYOUT.subPanel}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                Week ending {run.week_ending}
                <span style={{ marginLeft: 10, fontSize: 11, padding: '2px 10px', borderRadius: 999, background: `${C.gold}22`, color: C.gold, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {run.status}
                </span>
              </div>
              <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
                {run.driver_count ?? rows.length} drivers · {fmtMoney(run.total_payroll)}{' '}
                {blockedCount > 0 && <span style={{ color: C.red }}> · {blockedCount} blocked</span>}
                {' · '}{readyCount} ready
              </div>
              {run.notes && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>Notes: {run.notes}</div>}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {run.status === 'draft' && (
                <button className="btn btn-secondary" onClick={() => handleTransition('reviewed')}>Mark reviewed</button>
              )}
              {run.status === 'reviewed' && (
                <>
                  <button className="btn btn-ghost" onClick={() => handleTransition('draft')}>Back to draft</button>
                  <button className="btn btn-primary" onClick={() => handleTransition('approved')} disabled={blockedCount > 0}>
                    Approve
                  </button>
                </>
              )}
              {run.status === 'approved' && (
                <button className="btn btn-ghost" onClick={() => handleTransition('reviewed')}>Back to reviewed</button>
              )}
            </div>
          </div>
          {transitionError && <div style={{ marginTop: 8, fontSize: 12, color: C.red }}>{transitionError}</div>}
        </div>
      )}

      {/* Workbook rows */}
      {run && rows.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
            Workbook ({rows.length})
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {['Driver', 'Terminal', 'Rate', 'Type', 'Days', 'Base', 'Per diem', 'Stops bonus', 'Total', 'Status', ''].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.detail_row_id || i} style={{ borderTop: `1px solid ${C.borderSoft}`, background: r.blocking_reason ? 'rgba(255,107,107,0.06)' : undefined }}>
                    <td style={tdStyle}>
                      <div style={{ color: C.text }}>{r.last_name}, {r.first_name}</div>
                      {r.driver_id && <div style={{ fontSize: 10, color: C.textMuted }}>{r.driver_id}</div>}
                    </td>
                    <td style={tdStyle}>{r.terminal || '—'}</td>
                    <td style={{ ...tdStyle, color: r.missing_fields.includes('pay_rate') ? C.red : C.text }}>
                      {r.rate != null ? fmtMoney(r.rate) : '—'}
                    </td>
                    <td style={{ ...tdStyle, color: r.missing_fields.includes('rate_type') ? C.red : C.text }}>
                      {r.rate_type || '—'}
                    </td>
                    <td style={tdStyle}>{r.days ?? '—'}</td>
                    <td style={tdStyle}>{fmtMoney(r.base_pay)}</td>
                    <td style={tdStyle}>{fmtMoney(r.per_diem_pay)}</td>
                    <td style={tdStyle}>
                      {r.stop_bonus_eligible_stops != null
                        ? `${r.stop_bonus_eligible_stops} / ${fmtMoney(r.stop_bonus_amount)}`
                        : '—'}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: r.total_pay != null ? C.text : C.red }}>
                      {fmtMoney(r.total_pay)}
                    </td>
                    <td style={tdStyle}>
                      {r.blocking_reason ? (
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 999, background: `${C.red}22`, color: C.red, whiteSpace: 'nowrap' }}>
                          {r.blocking_reason}
                        </span>
                      ) : (
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 999, background: `${C.green}22`, color: C.green }}>
                          ready
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 11, padding: '4px 10px' }}
                        onClick={() => setAdjustingRow(r)}
                        disabled={run.status === 'exported'}
                      >
                        Adjust
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Adjustments log for this run */}
      {run && adjustments.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>
            Adjustments on this run ({adjustments.length})
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {adjustments.map((a: PayrollAdjustment) => (
              <div key={a.id} style={{ padding: 10, borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}`, fontSize: 12, display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
                <div>
                  <div style={{ color: C.text }}>
                    <span style={{ fontFamily: 'DM Mono, monospace' }}>{a.field_name}</span>
                    : {a.original_value || '—'} → <span style={{ fontWeight: 700 }}>{a.corrected_value}</span>
                  </div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                    <span style={{ color: classificationColor(a.classification) }}>{a.classification}</span>
                    {a.effective_date && ` · effective ${a.effective_date}`}
                    {a.reason && ` · ${a.reason}`}
                  </div>
                  <div style={{ fontSize: 11, color: C.textMuted }}>
                    {a.approved_by_email} · {new Date(a.approved_at).toLocaleString()}
                  </div>
                </div>
                <div style={{ fontSize: 10, color: C.textMuted, alignSelf: 'center' }}>
                  {a.linked_profile_id && <div>profile {a.linked_profile_id.slice(0, 8)}…</div>}
                  {a.linked_edit_log_id && <div>edit {a.linked_edit_log_id.slice(0, 8)}…</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {adjustingRow && run && (
        <PayrollAdjustmentSheet
          row={adjustingRow}
          runId={run.id}
          onClose={() => setAdjustingRow(null)}
          onSubmit={async (body) => postAdjustment(run.id, body)}
        />
      )}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, color: C.textMuted,
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
};
const inputStyle: React.CSSProperties = {
  padding: '6px 10px', fontSize: 13,
  background: C.panel, color: C.text,
  border: `1px solid ${C.borderSoft}`, borderRadius: 6,
};
const thStyle: React.CSSProperties = {
  padding: '6px 8px', textAlign: 'left',
  color: C.textMuted, fontWeight: 500, fontSize: 10,
  textTransform: 'uppercase', letterSpacing: '0.06em',
  borderBottom: `1px solid ${C.borderSoft}`, whiteSpace: 'nowrap',
};
const tdStyle: React.CSSProperties = {
  padding: '8px', color: C.text, whiteSpace: 'nowrap',
};
